import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, Inbox, TrendingUp, Plus, ArrowUpRight, MessageSquare, Database, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";

function StatCard({ label, value, hint, icon: Icon, accent = "pink", testId }) {
  const accentClass =
    accent === "teal" ? "from-brand-teal/20 to-brand-teal/5 text-brand-teal-deep" :
    accent === "ink" ? "from-ink/15 to-ink/5 text-ink" :
    "from-brand-pink/15 to-brand-pink/5 text-brand-pink-deep";
  return (
    <div data-testid={testId} className="rounded-2xl border border-mist bg-white p-6 sm:p-7">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${accentClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-5 text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">{label}</p>
      <div className="mt-2 font-display text-display-2 font-extrabold text-ink leading-none">{value}</div>
      {hint ? <p className="mt-2 text-xs text-ink/55">{hint}</p> : null}
    </div>
  );
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    adminApi.get("/admin/leads", { params: { limit: 6 } })
      .then((r) => setRecentLeads(r.data?.leads || []))
      .catch(() => {});
  }, []);

  const forceSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const r = await api.post("/admin/force-seed");
      setSeedResult(r.data?.results);
      const added = Object.values(r.data?.results || {}).reduce((s, v) => s + (v.added || 0), 0);
      toast.success(`Seed tamamlandı! ${added} yeni kayıt eklendi.`);
      // Refresh stats
      adminApi.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      toast.error("Seed başarısız: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="space-y-10">
      <Helmet><title>Dashboard · Yastık Medya Admin</title></Helmet>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-brand-pink-deep font-bold">Genel Bakış</span>
        <h1 className="mt-2 font-display text-display-2 font-bold text-ink leading-tight">Dashboard</h1>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-leads-total" label="Toplam Lead" value={stats?.leads_total ?? "—"}
          hint={`Bu ay: ${stats?.leads_month ?? 0}`} icon={Inbox} accent="pink" />
        <StatCard testId="stat-leads-week" label="Bu Hafta" value={stats?.leads_week ?? "—"}
          hint="Son 7 gün" icon={TrendingUp} accent="teal" />
        <StatCard testId="stat-articles" label="Yayında Yazı" value={stats?.articles_published ?? "—"}
          hint={`Taslak: ${stats?.articles_draft ?? 0}`} icon={FileText} accent="ink" />
        <StatCard testId="stat-cta-clicks" label="WhatsApp CTA" value={stats?.cta_clicks_week ?? "—"}
          hint="Tıklama / hafta" icon={MessageSquare} accent="pink" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent leads */}
        <div className="lg:col-span-2 rounded-2xl border border-mist bg-white p-6 sm:p-7">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-h2 font-semibold text-ink">Son lead'ler</h2>
            <Link to="/admin/leads" className="text-sm text-brand-pink-deep hover:underline inline-flex items-center gap-1">
              Tümü <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <ul className="mt-5 divide-y divide-mist">
            {recentLeads.length === 0 ? (
              <li className="py-6 text-sm text-ink/55">Henüz lead yok.</li>
            ) : recentLeads.map((l) => (
              <li key={l.id} className="py-3.5 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink truncate">{l.name}</p>
                  <p className="text-xs text-ink/55 truncate">{l.email} · {l.service}</p>
                </div>
                <span className="text-[0.65rem] uppercase tracking-[0.18em] font-bold text-ink/50 whitespace-nowrap">
                  {new Date(l.created_at).toLocaleDateString("tr-TR")}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick actions */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-mist bg-gradient-to-br from-brand-pink/10 to-brand-teal/10 p-6 sm:p-7">
            <h2 className="font-display text-h2 font-semibold text-ink">Hızlı eylemler</h2>
            <div className="mt-5 space-y-2.5">
              <Link to="/admin/blog/new" data-testid="quick-new-article"
                className="flex items-center justify-between gap-2 rounded-xl bg-ink text-cream px-4 py-3 text-sm font-semibold hover:bg-brand-pink-deep transition-colors">
                <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Yeni yazı</span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/admin/leads"
                className="flex items-center justify-between gap-2 rounded-xl bg-white border border-mist px-4 py-3 text-sm font-semibold text-ink hover:border-brand-pink transition-colors">
                <span className="inline-flex items-center gap-2"><Inbox className="h-4 w-4" /> Lead'leri görüntüle</span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href="/tr/blog" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between gap-2 rounded-xl bg-white border border-mist px-4 py-3 text-sm font-semibold text-ink hover:border-brand-pink transition-colors">
                <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Blog'a göz at</span>
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* DB Seed Panel */}
          <div className="rounded-2xl border-2 border-dashed border-brand-pink/30 bg-brand-pink/5 p-5">
            <h3 className="font-display text-sm font-bold text-ink flex items-center gap-2">
              <Database className="h-4 w-4 text-brand-pink" />
              Veritabanı Seed
            </h3>
            <p className="mt-2 text-xs text-ink/60 leading-relaxed">
              Production'da eksik içerikleri (blog, works) otomatik tamamlar. Mevcut veriyi silmez, sadece eksik olanları ekler.
            </p>
            <button
              type="button"
              onClick={forceSeed}
              disabled={seeding}
              data-testid="force-seed-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-pink text-cream px-4 py-2.5 text-sm font-semibold hover:bg-brand-magenta disabled:opacity-60 transition-colors"
            >
              {seeding ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Seed çalışıyor…</>
              ) : (
                <><Database className="h-4 w-4" /> Eksik İçerikleri Tamamla</>
              )}
            </button>
            {seedResult && (
              <div className="mt-3 space-y-1 text-xs">
                {Object.entries(seedResult).map(([coll, res]) => (
                  <div key={coll} className="flex items-center gap-2">
                    {res.error ? (
                      <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                    <span className="font-medium capitalize">{coll}:</span>
                    {res.error ? (
                      <span className="text-destructive">{res.error}</span>
                    ) : (
                      <span className="text-ink/60">
                        {res.added > 0 ? `+${res.added} eklendi (toplam ${res.after})` : `${res.after || res.count} mevcut`}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
